// Step-up ("Quick check: is it you?") for money endpoints. Billing answers
// 401 {"detail":{"error":"reauth_required"}} when the sign-in is older than
// 15 minutes. An axios response interceptor catches that, opens this modal
// (POST /auth/step-up/start -> code -> POST /auth/step-up/verify), then
// retries the original request exactly once with the fresh token.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui';
import { useAuth } from '../../context/useAuth.jsx';
import CodeBoxes from './CodeBoxes.jsx';
import { FormMsg, maskEmail } from './AuthShell.jsx';

const isReauthRequired = (error) =>
  error?.response?.status === 401 &&
  error?.response?.data?.detail?.error === 'reauth_required';

/**
 * Install the reauth interceptor on an axios instance.
 * requestStepUp() must resolve with the new access token, or reject if the
 * user cancels. Concurrent 401s share one prompt. Returns an uninstall fn.
 */
export function installStepUpInterceptor(instance, requestStepUp) {
  let pending = null;
  const id = instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const config = error?.config;
      if (!isReauthRequired(error) || !config || config.__stepUpRetried) {
        return Promise.reject(error);
      }
      config.__stepUpRetried = true; // the retry happens exactly once
      try {
        if (!pending) {
          pending = Promise.resolve(requestStepUp()).finally(() => { pending = null; });
        }
        const newToken = await pending;
        config.headers = config.headers || {};
        if (typeof config.headers.set === 'function') config.headers.set('Authorization', `Bearer ${newToken}`);
        else config.headers.Authorization = `Bearer ${newToken}`;
        return instance.request(config);
      } catch (stepUpError) {
        return Promise.reject(error);
      }
    }
  );
  return () => instance.interceptors.response.eject(id);
}

export default function StepUpModal({ open, email, onSuccess, onCancel }) {
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [expired, setExpired] = useState(false);
  const started = useRef(false);
  const dialogRef = useRef(null);
  const returnFocus = useRef(null);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  // Capture the opener during render: the code box's autoFocus runs at commit,
  // before any effect, and would otherwise hide it.
  if (open && !returnFocus.current) returnFocus.current = document.activeElement;

  const sendCode = useCallback(async () => {
    setMsg('');
    setExpired(false);
    setCode('');
    try {
      await axios.post('/auth/step-up/start', undefined, { withCredentials: true });
    } catch (err) {
      setMsg("We couldn't send the code. Please try again.");
    }
  }, []);

  useEffect(() => {
    if (open && !started.current) {
      started.current = true;
      sendCode();
    }
    if (!open) { started.current = false; setCode(''); setMsg(''); setBusy(false); }
  }, [open, sendCode]);

  // Focus: remember the opener, move into the dialog, trap Tab, close on
  // Escape (= cancel), and hand focus back on close.
  useEffect(() => {
    if (!open) return undefined;
    const node = dialogRef.current;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); cancelRef.current(); return; }
      if (e.key !== 'Tab' || !node) return;
      const items = node.querySelectorAll('button:not([disabled]), input:not([disabled])');
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      else if (!node.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const back = returnFocus.current;
      returnFocus.current = null;
      if (back && typeof back.focus === 'function') back.focus();
    };
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (code.length !== 6 || busy) return;
    setBusy(true);
    setMsg('');
    try {
      const res = await axios.post('/auth/step-up/verify', { code }, { withCredentials: true });
      setBusy(false);
      onSuccess(res.data);
    } catch (err) {
      setBusy(false);
      const status = err?.response?.status;
      if (status === 410) {
        setExpired(true);
        setMsg('That code has expired.');
      } else if (status === 400) {
        const n = err.response.data?.attempts_left;
        setMsg(typeof n === 'number'
          ? `That code isn't right. ${n} ${n === 1 ? 'try' : 'tries'} left.`
          : "That code isn't right.");
        setCode('');
      } else {
        setMsg('Something went wrong. Please try again in a moment.');
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50" role="dialog" aria-modal="true" aria-labelledby="stepup-title">
      <form ref={dialogRef} onSubmit={submit} className="w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-6 shadow-xl">
        <h2 id="stepup-title" className="text-2xl font-semibold text-gray-900">Quick check: is it you?</h2>
        <p className="text-sm text-gray-600 mt-2 mb-5">
          Before anything involving money, we confirm it's really you. We've emailed a 6-digit code to{' '}
          <b className="text-gray-900">{(email && maskEmail(email)) || 'your email address'}</b>.
        </p>
        <CodeBoxes value={code} onChange={setCode} disabled={busy} label="Digit" />
        <FormMsg error>{msg}</FormMsg>
        <Button type="submit" variant="primary" size="lg" className="w-full mt-2" disabled={code.length !== 6 || busy}>
          Confirm and continue
        </Button>
        <div className="flex justify-between items-center mt-4 text-sm">
          <span className="text-gray-500">We won't ask again for 15 minutes</span>
          <button type="button" className="font-semibold underline" onClick={sendCode} data-testid="stepup-resend">
            {expired ? 'Send a new code' : 'Resend code'}
          </button>
        </div>
        <button type="button" className="mt-4 text-sm text-gray-500 underline" onClick={onCancel}>Cancel</button>
      </form>
    </div>
  );
}

/**
 * Mount once inside <AuthProvider>. Wires the global axios interceptor to
 * the modal and feeds the new token back into AuthContext.
 */
export function StepUpHost() {
  const { setToken, user } = useAuth();
  const [open, setOpen] = useState(false);
  const waiter = useRef(null);

  useEffect(() => {
    const request = () => new Promise((resolve, reject) => {
      waiter.current = { resolve, reject };
      setOpen(true);
    });
    return installStepUpInterceptor(axios, request);
  }, []);

  const close = () => { setOpen(false); };
  return (
    <StepUpModal
      open={open}
      email={user?.email}
      onSuccess={(data) => {
        setToken(data.access_token, data.expires_in || 900);
        close();
        waiter.current?.resolve(data.access_token);
        waiter.current = null;
      }}
      onCancel={() => {
        close();
        waiter.current?.reject(new Error('step_up_cancelled'));
        waiter.current = null;
      }}
    />
  );
}
